import React, { useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { withEditMode, withEditModeNotice } from '_containers/BaseComponent';
import { ITheme } from '_containers/Theme';
import { useTranslation } from 'react-i18next';
import Theme from '_containers/Theme/theme';

import { compose } from 'ramda';
import { Text } from '@sitecore-jss/sitecore-jss-react';

import {
  Container,
  Heading,
  List,
  FeatureListItem,
  ShowMoreItem,
  FeatureTitle,
  FeatureDescription,
  FeatureIconContainer,
  FeatureTickWrapper,
  FeatureCrossWrapper,
  FeatureIconPosition
} from './StyledFeatureList';

import Tick from '_utils/icons/Tick';
import Cross from '_utils/icons/Cross';

// import data from './mockFeatures.json';
import Grid from '_utils/components/Corporate/Grid';
import ScreenReader from '_utils/components/Corporate/ScreenReader';
import { throwInDev } from '_utils/helpers/dev';
import ContentRichText from '../ContentRichText';

import ShowMore from '_utils/components/Corporate/ShowMore';
import PropTypes from 'prop-types';

const MissingDataSource = compose(withEditModeNotice)(styled.div``);

type FeatureData = {
  title: string;
  description?: string;
  included?: boolean;
};

const featuresTheme: ITheme = {
  ...Theme,
  grid: {
    ...Theme.grid,
    gutter: {
      size: {
        xs: 16
      },
      directions: ['left', 'right', 'top', 'bottom']
    }
  }
};

const getIncluded = (value) => {
  switch (value) {
    case 'Yes':
      return true;
    case 'No':
      return false;
    case 'Not applicable':
    case '':
      return null;
    default:
      throwInDev('unrecognized feature list included value ' + value);
  }
};

const FeatureListUl = ({ title, features, editMode }) => {
  const primaryFeatures: FeatureData[] = editMode ? features : features.slice(0, 4);
  const secondaryFeatures: FeatureData[] = editMode ? [] : features.slice(4);
  const [canShowMore, setCanShowMore] = useState(
    !editMode && secondaryFeatures && secondaryFeatures.length > 0
  );
  const [showingMore, setShowingMore] = useState(false);
  const [t] = useTranslation();

  return (
    <List as="ul">
      {primaryFeatures.map((data, index) => (
        <FeatureItem key={`primary-feature-${index}`} data={data} />
      ))}
      {canShowMore && (
        <ShowMoreItem as="li" $config={{ col: { xs: 12 } }}>
          <ShowMore
            title={title}
            label={t('feature-list-show-more-label')}
            count={secondaryFeatures.length}
            onClick={() => {
              setShowingMore(true);
              setCanShowMore(false);
            }}
          />
        </ShowMoreItem>
      )}
      {secondaryFeatures.map((data: FeatureData, index) => (
        <FeatureItem
          editMode={editMode}
          hide={!showingMore}
          key={`secondary-feature-${index}`}
          data={data}
        />
      ))}
    </List>
  );
};

FeatureListUl.propTypes = {
  title: PropTypes.string,
  features: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.shape({
        jss: PropTypes.shape({
          fields: PropTypes.shape({
            displayName: PropTypes.shape({
              value: PropTypes.string.isRequired,
            }).isRequired,
          }).isRequired,
        }).isRequired,
      }).isRequired,
      description: PropTypes.shape({
        jss: PropTypes.shape({
          value: PropTypes.string
        })
      }),
      included: PropTypes.bool,
    })
  ).isRequired,
  editMode: PropTypes.bool.isRequired,
};

const FeatureItem = (props) => {
  const [t] = useTranslation();
  const isIncluded = getIncluded(props.data?.availability?.value);
  const hide = props.hide;
  const editMode = false;
  const title = props.data?.title?.jss?.fields?.displayName?.value;
  const description = props.data?.description?.jss?.value;
  return (
    <FeatureListItem
      className={hide ? 'is-hidden' : ''}
      as="li"
      $config={{ col: { xs: 12, md: 6 } }}
    >
      {isIncluded !== null && (
        <FeatureIconPosition>
          <FeatureIconContainer>
            {isIncluded === true && (
              <FeatureTickWrapper>
                <Tick ariaHidden={true} />
              </FeatureTickWrapper>
            )}
            {isIncluded === false && (
              <FeatureCrossWrapper>
                <Cross ariaHidden={true} />
              </FeatureCrossWrapper>
            )}
          </FeatureIconContainer>
        </FeatureIconPosition>
      )}
      {editMode ? (
        <Text field={{ value: title }} editable={true} />
      ) : (
        <FeatureTitle>
          {title}
          {isIncluded === true && (
            <ScreenReader> {t('feature-list-accessible-included-suffix')}</ScreenReader>
          )}
          {isIncluded === false && (
            <ScreenReader> {t('feature-list-accessible-excluded-suffix')}</ScreenReader>
          )}
        </FeatureTitle>
      )}
      {description && (
        <FeatureDescription>
          <ContentRichText tag="span" field={{ value: description }} />
        </FeatureDescription>
      )}
    </FeatureListItem>
  );
};

FeatureItem.propTypes = {
  data: PropTypes.shape({
    availability: PropTypes.shape({
      value: PropTypes.string,
    }),
    title: PropTypes.shape({
      jss: PropTypes.shape({
        fields: PropTypes.shape({
          displayName: PropTypes.shape({
            value: PropTypes.string,
          }),
        }),
      }),
    }),
    description: PropTypes.shape({
      jss: PropTypes.shape({
        value: PropTypes.string,
      }),
    }),
  }).isRequired,
  hide: PropTypes.bool,
  editMode: PropTypes.bool,
};

const FeatureList = (props) => {
  const { editMode, rendering } = props;
  const featuresItems = rendering?.fields?.data?.datasource?.featureItems;
  if (editMode) {
    if (!featuresItems) {
      return (
        <MissingDataSource
          noticeEnabled={true}
          noticeMessage={`Features List has no data source`}
        />
      );
    }

    if (!featuresItems.length) {
      const EditContainer = compose(withEditModeNotice)(styled.div``);
      return (
        <EditContainer
          noticeEnabled
          noticeLevel="warning"
          noticeMessage={'Features List component has no items and will not be displayed'}
        />
      );
    }
  }
  if (!featuresItems || !featuresItems.length) {
    return null;
  }
  const title = rendering?.fields?.data?.datasource?.title?.jss?.value; //will be overridden

  const id = rendering?.dataSource || '' + Math.random();
  return (
    <Container key={id}>
      <ThemeProvider theme={featuresTheme}>
        {title && (
          <Grid.Row $config={{ gutters: ['left', 'right'] }}>
            <Grid.Item $config={{ gutters: ['left', 'right'] }}>
              {editMode ? (
                <Heading>
                  <Text field={rendering?.fields?.data?.datasource?.title.jss} editable={true} />
                </Heading>
              ) : (
                <Heading>{title}</Heading>
              )}
            </Grid.Item>
          </Grid.Row>
        )}
        <FeatureListUl title={title} features={featuresItems} editMode={editMode} />
      </ThemeProvider>
    </Container>
  );
};

FeatureList.propTypes = {
  editMode: PropTypes.bool,
  rendering: PropTypes.shape({
    fields: PropTypes.shape({
      data: PropTypes.shape({
        datasource: PropTypes.shape({
          featureItems: PropTypes.arrayOf(
            PropTypes.shape({
              availability: PropTypes.shape({
                value: PropTypes.string,
              }),
              title: PropTypes.shape({
                jss: PropTypes.shape({
                  fields: PropTypes.shape({
                    displayName: PropTypes.shape({
                      value: PropTypes.string,
                    }),
                  }),
                }),
              }),
              description: PropTypes.shape({
                jss: PropTypes.shape({
                  value: PropTypes.string,
                }),
              }),
            })
          ),
          title: PropTypes.shape({
            jss: PropTypes.shape({
              value: PropTypes.string,
            }),
          }),
        }),
      }),
    }),
    dataSource: PropTypes.string,
  }),
};

export default compose(withEditMode)(FeatureList);
